import BaseDataFilter from "@/core/js/backend/filters/BaseDataFilter";
import DataFilter from "@/core/js/backend/filters/DataFilter";
import {JournalEntryType} from "@shared/Journal/JournalEntryType";
import {getPlannedSessionServiceItems} from "@shared/Journal/TlswSessionPlanMismatchHelper";
import {isAppTheraLogik} from "@/utils/helpers";
import DraftLineItemManager from "@tl/core/modules/commission/model/commission/DraftLineItemManager";

const MIGRATION_PAGE_SIZE = 100
const MINUTE_MS = 60 * 1000

function utcTimeOfDayMatches(left, right) {
	return left.getUTCHours() === right.getUTCHours()
		&& left.getUTCMinutes() === right.getUTCMinutes()
		&& left.getUTCSeconds() === right.getUTCSeconds()
		&& left.getUTCMilliseconds() === right.getUTCMilliseconds()
}

function localTimeOfDayMatches(left, right) {
	return left.getHours() === right.getHours()
		&& left.getMinutes() === right.getMinutes()
		&& left.getSeconds() === right.getSeconds()
		&& left.getMilliseconds() === right.getMilliseconds()
}

export function correctedAutoSessionTime(startTimeMs, anchorTimeMs) {
	let saved = new Date(startTimeMs)
	let anchor = new Date(anchorTimeMs)
	if (!Number.isFinite(saved.getTime()) || !Number.isFinite(anchor.getTime())) return null
	if (!utcTimeOfDayMatches(saved, anchor)) return null

	let offsetChangeMinutes = saved.getTimezoneOffset() - anchor.getTimezoneOffset()
	let correctedTimeMs = startTimeMs + offsetChangeMinutes * MINUTE_MS
	let corrected = new Date(correctedTimeMs)
	return localTimeOfDayMatches(corrected, anchor) ? correctedTimeMs : null
}

function belongsToSessionPlan(session, plannedServiceIds) {
	if (plannedServiceIds.has(session.sourceServiceId)) return true
	return !session.sourceServiceId && !!session.getEntryByType?.(JournalEntryType.TLSW_SESSION_PLAN)
}

async function syncLinkedScheduledEvent(dataManager, session) {
	if (!session.scheduledEventId) return
	let event = await dataManager.getScheduledEventItem(session.scheduledEventId)
	if (!event || event.startTime === session.startTimeMs) return
	event.startTime = session.startTimeMs
	await dataManager.updateScheduledEventItem(event)
}

async function migrateCommissionSessionTimes(dataManager, clientProfile, draftManager) {
	let commission = clientProfile.commissionDetails
	let anchorTimeMs = commission?.sessionStartTimeMs
	if (!anchorTimeMs || commission.sessionTimeMigrationDone) return
	let plannedServiceIds = new Set(getPlannedSessionServiceItems(commission.getQuoteItem()).map(item => item.id))
	let pager = await dataManager.getJournalEntryListPager(
		new DataFilter(MIGRATION_PAGE_SIZE)
			.where("clientId", "==", clientProfile.id)
			.where("type", "==", JournalEntryType.SESSION)
	)
	let changedCount = 0
	let unresolvedCount = 0
	while (pager.hasMore) {
		let sessions = await pager.next()
		let changedSessions = []
		for (let session of sessions) {
			if (!session.startTimeMs || session.isManuallyScheduled || session.skipAutoSchedule) continue
			if (!belongsToSessionPlan(session, plannedServiceIds)) continue
			let correctedTimeMs = correctedAutoSessionTime(session.startTimeMs, anchorTimeMs)
			if (correctedTimeMs === null) {
				unresolvedCount++
				continue
			}
			if (correctedTimeMs === session.startTimeMs) continue
			session.startTimeMs = correctedTimeMs
			changedSessions.push(session)
		}
		if (!changedSessions.length) continue
		for (let session of changedSessions) {
			await syncLinkedScheduledEvent(dataManager, session)
			await draftManager.syncSessionDraftUsage(session, clientProfile)
		}
		await dataManager.batchUpdateJournalEntry(changedSessions)
		changedCount += changedSessions.length
	}
	let upcomingIndex = commission.progressInfo?.upcomingSessionIndex
	if (changedCount && upcomingIndex) {
		upcomingIndex.isNextActiveItemFetched = false
		upcomingIndex.nextActiveItem = null
	}
	commission.sessionTimeMigrationDone = true
	await dataManager.updateClientProfile(clientProfile)
	if (unresolvedCount) console.warn("SESSION_TIME_MIGRATION unresolved", JSON.stringify({
		clientId: clientProfile.id, unresolvedCount,
	}))
}

export async function migrateSessionTimes(dataManager, authManager) {
	if (!isAppTheraLogik || !authManager.isCoach()) return
	let draftManager = new DraftLineItemManager(dataManager)
	let pager = await dataManager.getClientProfileListPager(new BaseDataFilter(MIGRATION_PAGE_SIZE))
	while (pager.hasMore) {
		let clientProfiles = await pager.next()
		for (let clientProfile of clientProfiles) {
			await migrateCommissionSessionTimes(dataManager, clientProfile, draftManager)
		}
	}
}
